/**
 * Copyright 2025-2026 ForgeRock AS. All Rights Reserved
 *
 * Use of this code requires a commercial software license with ForgeRock AS
 * or with one of its affiliates. All use shall be exclusively subject
 * to such license between the licensee and ForgeRock AS.
 */

import { cloneDeep, reject } from 'lodash';
import store from '@/store';
import router from '../router';
/**
    * Import the menu json file. The menu file defaults to menus.platform.json
    * but can be overridden using the VUE_APP_MENUS_FILE environment
    * variable. The file extension is included here so webpack can anticipate
    * which files to include.
    */
export default async function setAdminMenuItems() {
  try {
    // webpackIgnore is used below to exclude the menus.js file from being
    // minified and bundled by webpack. Instead it will leave the menu file
    // as is, in its original location which allows for customisation.
    let menus = await import(/* webpackIgnore: true */ `../config/menus/${store.state.menusFile}.js`);
    let menuItems = [];
    let tenantMenuItems = [];
    let realmMenuItems = [];
    menus = cloneDeep(menus.default);
    menuItems = menus.sideMenu;

    if (menus.tenantMenu) {
      tenantMenuItems = menus.tenantMenu;
    }
    const nativeConsoleLinks = {
      displayName: 'sideMenu.nativeConsoles',
      icon: 'open_in_new',
      subItems: [],
    };

    // we only want to display the realm menu and native am console links if there is an am url defined
    if (store.state.SharedStore.hasAmUrl) {
      realmMenuItems = menus.realmMenu;
      // add the am admin url to the nativeConsoleLinks.subitems
      nativeConsoleLinks.subItems.push({
        displayName: 'sideMenu.accessManagement',
        url: `${store.getters.amPrefix}/dashboard`,
        showForRoles: ['realmAdmin', 'amAdmin'],
      });
    }

    // show new dashboard sideMenu only if mode is equal to beta
    const isBetaAnalyticsMode = store.state.analyticsDashboardMode === 'beta';
    if (store.state.analyticsDashEnabled && isBetaAnalyticsMode) {
      const analyticsDashboardMenu = {
        displayName: 'sideMenu.dashboard',
        icon: 'dashboard',
        subItems: [
          {
            displayName: 'sideMenu.realmOverview',
            routeTo: { name: 'RealmOverview' },
          },
          {
            displayName: 'sideMenu.journeys',
            routeTo: { name: 'JourneyMetrics' },
          },
        ],
      };
      // Replacing the old sidemenu Dashboard with new menus
      menuItems.splice(0, 1, analyticsDashboardMenu);
    }

    // handle menu items for auto access
    if (store.state.SharedStore.autoAccessEnabled) {
      const subItems = [
        {
          displayName: 'sideMenu.realmOverview',
          routeTo: { name: isBetaAnalyticsMode ? 'RealmOverview' : 'Dashboard' },
        },
      ];

      // Add Journeys Dashboard only in beta mode
      if (isBetaAnalyticsMode) {
        subItems.push({
          displayName: 'sideMenu.journeys',
          routeTo: { name: 'JourneyMetrics' },
        });
      }

      subItems.push(
        {
          displayName: 'sideMenu.risk',
          routeTo: { name: 'AutoAccessRisk' },
        },
        {
          displayName: 'sideMenu.authBehavior',
          routeTo: { name: 'AutoAccessTenantBehavior' },
        },
      );

      const autoAccessDashboardMenu = {
        displayName: 'sideMenu.dashboard',
        icon: 'dashboard',
        subItems,
      };

      const autoAccessAdminMenu = {
        displayName: 'sideMenu.riskAdministration',
        icon: 'settings',
        subItems: [
          {
            displayName: 'sideMenu.dataSources',
            routeTo: { name: 'AutoAccessDataSources' },
          },
          {
            displayName: 'sideMenu.pipelines',
            routeTo: { name: 'AutoAccessPipelines' },
          },
          {
            displayName: 'sideMenu.riskConfig',
            routeTo: { name: 'AutoAccessRiskConfig' },
          },
        ],
      };
      menuItems.splice(0, 1, autoAccessDashboardMenu);
      menuItems.splice(8, 0, autoAccessAdminMenu);
    }

    // add the idm admin url to the nativeConsoleLinks.subitems for AIC
    if (store.state.isFraas) {
      nativeConsoleLinks.subItems.push({
        displayName: 'sideMenu.identityManagement',
        url: store.state.realm === 'root' ? store.state.idmAdminURL : `${store.state.idmAdminURL}?realm=${store.state.realm}`,
        showForRoles: ['adminUser'],
      });
    }
    // Add native console links to menuItems only if there are subItems
    if (nativeConsoleLinks.subItems.length) menuItems.push(nativeConsoleLinks);

    // If this isFraas and workforce is not enabled remove the Events menu item
    if (store.state.isFraas && !store.state.SharedStore.workforceEnabled) {
      menuItems = reject(menuItems, (item) => item.routeTo?.name === 'Events');
    }
    // if userPermissions are defined this user is not a super or tenant admin so we need to show/hide menu items according to policy
    if (store.state.userPermissions && !store.state.userPermissions.menuItems['*']?.READ) {
      menuItems = reject(menuItems, (item) => !store.state.userPermissions.menuItems[item.displayName]?.READ ?? false);
      menuItems.forEach((item) => {
        if (item.subItems) {
          item.subItems = reject(item.subItems, (subItem) => !store.state.userPermissions.menuItems[subItem.displayName]?.READ ?? false);
        }
      });
      if (menuItems.length > 0) {
        // set the defaultRoute to the route of the first displayed menu item then navigate to that route
        const defaultRoute = router.getRoutes().find((route) => route.name === menuItems[0].routeTo.name);
        store.commit('setDefaultRoute', defaultRoute);
      }
    }

    store.commit('setMenuItems', menuItems);
    store.commit('setTenantMenuItems', tenantMenuItems);
    store.commit('setRealmMenuItems', realmMenuItems);
  } catch (error) {
    console.error('Error loading menus file:', error); // eslint-disable-line no-console
  }
}
